/**
 * Runtime Configuration Service
 * 
 * This module provides a unified way to access environment variables that works
 * both in local development (via process.env from webpack) and in deployed
 * environments (via window.__ENV__ injected at container startup).
 * 
 * USAGE:
 *   import { getEnvVar, ENV } from '../config/RuntimeConfig';
 *   
 *   // Option 1: Direct access via getEnvVar
 *   const apiUrl = getEnvVar('LAYOUT_API_URL');
 *   
 *   // Option 2: Pre-built ENV object for common variables
 *   const apiUrl = ENV.LAYOUT_API_URL;
 * 
 * PRIORITY ORDER:
 *   1. window.__ENV__ (runtime injection from env.js)
 *   2. process.env (build-time from webpack/dotenv)
 * 
 * LOCAL DEVELOPMENT:
 *   - Continue using .env.local files as before
 *   - Webpack DefinePlugin injects values into process.env
 *   - window.__ENV__ will be undefined, so process.env is used
 * 
 * DEPLOYED ENVIRONMENTS:
 *   - env.js is generated at container startup via envsubst
 *   - window.__ENV__ is populated with values from K8s ConfigMap/Secrets
 *   - These take precedence over any build-time values
 */

// Extend Window interface to include __ENV__
declare global {
  interface Window {
    __ENV__?: Record<string, string | undefined>;
  }
}

/**
 * Get the runtime configuration object (window.__ENV__)
 * Returns an empty object if not available (local development)
 */
const getRuntimeConfig = (): Record<string, string | undefined> => {
  if (typeof window !== 'undefined' && window.__ENV__) {
    return window.__ENV__;
  }
  return {};
};

type RuntimeEnvRecord = Record<string, string | undefined>;

/**
 * Get an environment variable value.
 * Checks runtime config (window.__ENV__) first, then falls back to process.env.
 * 
 * @param key - The environment variable name
 * @param defaultValue - Optional default value if not found
 * @returns The value or undefined
 */
export const getEnvVar = (key: string, defaultValue?: string): string | undefined => {
  const runtimeConfig = getRuntimeConfig();
  
  // Check runtime config first (deployed environments)
  if (runtimeConfig[key] !== undefined && runtimeConfig[key] !== '') {
    // Handle the case where envsubst didn't replace the placeholder
    // (value still looks like "${VARIABLE}")
    const value = runtimeConfig[key];
    if (value && !value.startsWith('${')) {
      return value;
    }
  }
  
  // Fall back to build-time process.env (local development)
  const processEnvValue = process.env[key];
  if (processEnvValue !== undefined && processEnvValue !== '') {
    return processEnvValue;
  }
  
  return defaultValue;
};

const getScriptEnvValue = (key: string): string | undefined => getEnvVar(key);

const getScriptEnvSnapshot = (): RuntimeEnvRecord => {
  const runtimeConfig = getRuntimeConfig();
  const processEnv = (process.env || {}) as RuntimeEnvRecord;
  const keys = Array.from(new Set([
    ...Object.keys(processEnv),
    ...Object.keys(runtimeConfig),
  ]));

  const snapshot: RuntimeEnvRecord = {};
  for (const key of keys) {
    const value = getScriptEnvValue(key);
    if (value !== undefined) {
      snapshot[key] = value;
    }
  }

  return snapshot;
};

/**
 * Script-safe env adapter for evaluated custom JS.
 * It preserves the familiar `env.MY_KEY` access pattern while resolving values
 * from runtime config first and process.env second.
 */
export const SCRIPT_ENV = new Proxy<RuntimeEnvRecord>({} as RuntimeEnvRecord, {
  get(_target, prop) {
    if (prop === 'toJSON') {
      return () => getScriptEnvSnapshot();
    }

    if (typeof prop !== 'string') {
      return undefined;
    }

    return getScriptEnvValue(prop);
  },
  has(_target, prop) {
    return typeof prop === 'string' && getScriptEnvValue(prop) !== undefined;
  },
  ownKeys() {
    return Reflect.ownKeys(getScriptEnvSnapshot());
  },
  getOwnPropertyDescriptor(_target, prop) {
    if (typeof prop !== 'string') {
      return undefined;
    }

    const value = getScriptEnvValue(prop);
    if (value === undefined) {
      return undefined;
    }

    return {
      configurable: true,
      enumerable: true,
      value,
      writable: false,
    };
  },
});

/**
 * Get an environment variable as a boolean.
 * Treats 'true', '1', 'yes' as true; everything else as false.
 */
export const getEnvVarBool = (key: string, defaultValue = false): boolean => {
  const value = getEnvVar(key);
  if (value === undefined) return defaultValue;
  return ['true', '1', 'yes'].includes(value.toLowerCase());
};

/**
 * Get an environment variable as a number.
 */
export const getEnvVarNumber = (key: string, defaultValue?: number): number | undefined => {
  const value = getEnvVar(key);
  if (value === undefined) return defaultValue;
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

/**
 * Pre-built ENV object with getters for common environment variables.
 * This provides IntelliSense support and type safety.
 * 
 * Each property is a getter that calls getEnvVar() at access time,
 * ensuring we always get the current value.
 */
export const ENV = {
  // ===== CORE CONFIG =====
  get NODE_ENV() { return getEnvVar('NODE_ENV'); },
  get ENVIRONMENT() { return getEnvVar('ENVIRONMENT'); },
  get PUBLIC_URL() { return getEnvVar('PUBLIC_URL'); },
  get IS_RUNTIME() { return getEnvVar('IS_RUNTIME'); },
  get PROJECT() { return getEnvVar('PROJECT'); },

  // ===== API ENDPOINTS =====
  get LAYOUT_API_URL() { return getEnvVar('LAYOUT_API_URL'); },
  get LAYOUT_API_URL_RUNTIME() { return getEnvVar('LAYOUT_API_URL_RUNTIME'); },
  get FORM_SERVICE_API_URL() { return getEnvVar('FORM_SERVICE_API_URL'); },
  get ADMIN_SERVICE_API_URL() { return getEnvVar('ADMIN_SERVICE_API_URL'); },
  get EMAIL_URL() { return getEnvVar('EMAIL_URL'); },
  get SEND_EMAIL_URL() { return getEnvVar('SEND_EMAIL_URL'); },
  get API_HOST() { return getEnvVar('API_HOST'); },

  // ===== FILE/DOCUMENT SERVICES =====
  get DSS_BASEURL() { return getEnvVar('DSS_BASEURL'); },
  get DSS_APIKEY() { return getEnvVar('DSS_APIKEY'); },
  get APIM_SUBSCRIPTION_KEY() { return getEnvVar('APIM_SUBSCRIPTION_KEY'); },
  get DOWNLOAD_RECEIPT_URL() { return getEnvVar('DOWNLOAD_RECEIPT_URL'); },
  get DOWNLOAD_SUMMARY_URL() { return getEnvVar('DOWNLOAD_SUMMARY_URL'); },

  // ===== ADDRESS LOOKUP =====
  get PC_LOOKUP_URL() { return getEnvVar('PC_LOOKUP_URL'); },
  get POSTAL_CODE_LOOKUP_URL() { return getEnvVar('POSTAL_CODE_LOOKUP_URL'); },
  get ONTARIO_IP_URL() { return getEnvVar('ONTARIO_IP_URL'); },

  // ===== AUTHENTICATION =====
  get PUBLIC_SECURE_ISSUER() { return getEnvVar('PUBLIC_SECURE_ISSUER'); },
  get PUBLIC_SECURE_CLIENTID() { return getEnvVar('PUBLIC_SECURE_CLIENTID'); },
  get PUBLIC_SECURE_SIGNOUT_REDIRECT_URI() { return getEnvVar('PUBLIC_SECURE_SIGNOUT_REDIRECT_URI'); },
  get PUBLIC_SECURE_SESSION_CHECK_INTERVAL() { return getEnvVar('PUBLIC_SECURE_SESSION_CHECK_INTERVAL'); },
  get PUBLIC_SECURE_PROFILE_MANAGE_URI() { return getEnvVar('PUBLIC_SECURE_PROFILE_MANAGE_URI'); },
  get PUBLIC_SECURE_USER_SETTINGS_URI() { return getEnvVar('PUBLIC_SECURE_USER_SETTINGS_URI'); },
  get REDIRECT_URL() { return getEnvVar('REDIRECT_URL'); },
  get ISSUER() { return getEnvVar('ISSUER'); },
  get CLIENT_ID() { return getEnvVar('CLIENT_ID'); },
  get BASE_URI() { return getEnvVar('BASE_URI'); },
  get REDIRE_URL() { return getEnvVar('REDIRE_URL'); },
  get clientId() { return getEnvVar('clientId'); },
  get redirectUrl() { return getEnvVar('redirectUrl'); },
  get redirectUri() { return getEnvVar('redirectUri'); },
  get LoginCallbackUrl() { return getEnvVar('LoginCallbackUrl'); },

  // ===== BPS AUTHENTICATION =====
  get BPS_BASE_URL() { return getEnvVar('BPS_BASE_URL'); },
  get BPS_REDIRECT_URI() { return getEnvVar('BPS_REDIRECT_URI'); },

  // ===== CXP INTEGRATION (English) =====
  get PUBLIC_CXP_WEB_BASE_PATH() { return getEnvVar('PUBLIC_CXP_WEB_BASE_PATH'); },
  get PUBLIC_CXP_API_BASE_PATH() { return getEnvVar('PUBLIC_CXP_API_BASE_PATH'); },
  get PUBLIC_CXP_SIGN_URI() { return getEnvVar('PUBLIC_CXP_SIGN_URI'); },
  get CXP_URL() { return getEnvVar('CXP_URL'); },
  get CXP_INBOX() { return getEnvVar('CXP_INBOX'); },
  get CXP_MYACCOUNT() { return getEnvVar('CXP_MYACCOUNT'); },
  get CXP_MYTRANSACTION() { return getEnvVar('CXP_MYTRANSACTION'); },
  get CXP_SIGNOUT() { return getEnvVar('CXP_SIGNOUT'); },
  get SERVICEONTARIO_URL() { return getEnvVar('SERVICEONTARIO_URL'); },

  // ===== CXP INTEGRATION (French) =====
  get CXP_URL_F() { return getEnvVar('CXP_URL_F'); },
  get CXP_INBOX_F() { return getEnvVar('CXP_INBOX_F'); },
  get CXP_MYACCOUNT_F() { return getEnvVar('CXP_MYACCOUNT_F'); },
  get CXP_MYTRANSACTION_F() { return getEnvVar('CXP_MYTRANSACTION_F'); },
  get CXP_SIGNOUT_F() { return getEnvVar('CXP_SIGNOUT_F'); },
  get SERVICEONTARIO_URL_F() { return getEnvVar('SERVICEONTARIO_URL_F'); },

  // ===== BXP INTEGRATION =====
  get BXP_UI_BASE_PATH() { return getEnvVar('BXP_UI_BASE_PATH'); },

  // ===== OTRIS SPECIFIC =====
  get PUBLIC_OTRIS_API_BASE_PATH() { return getEnvVar('PUBLIC_OTRIS_API_BASE_PATH'); },

  // ===== RECAPTCHA =====
  get RECAPTCHA_SITE_KEY() { return getEnvVar('RECAPTCHA_SITE_KEY'); },
  get DISABLED_RECAPTCHA() { return getEnvVar('DISABLED_RECAPTCHA'); },

  // ===== SESSION MANAGEMENT =====
  get SESSION_DURATION() { return getEnvVar('SESSION_DURATION'); },

  // ===== GOOGLE SERVICES =====
  get GOOGLE_MAPS_API_KEY() { return getEnvVar('GOOGLE_MAPS_API_KEY'); },

  // ===== MONITORING / APPLICATION INSIGHTS =====
  get APPLICATIONINSIGHTS_CONNECTION_STRING() { return getEnvVar('APPLICATIONINSIGHTS_CONNECTION_STRING'); },
  get APPLICATIONINSIGHTS_ROLE_NAME() { return getEnvVar('APPLICATIONINSIGHTS_ROLE_NAME'); },

  // ===== ADMIN SERVICE =====
  get ADMIN_SERVICE_API_KEY() { return getEnvVar('ADMIN_SERVICE_API_KEY'); },

  // ===== DYNAMIC SCRIPTS =====
  get DYNAMIC_SCRIPT_ENABLED() { return getEnvVar('DYNAMIC_SCRIPT_ENABLED'); },
  get DYNAMIC_SCRIPT_NAME() { return getEnvVar('DYNAMIC_SCRIPT_NAME'); },

  // ===== FEEDBACK URLS =====
  get FEEDBACK_URL_CXP_USER() { return getEnvVar('FEEDBACK_URL_CXP_USER'); },
  get FEEDBACK_URL_NO_CXP_USER() { return getEnvVar('FEEDBACK_URL_NO_CXP_USER'); },
  get FEEDBACK_URL_CXP_USER_F() { return getEnvVar('FEEDBACK_URL_CXP_USER_F'); },
  get FEEDBACK_URL_NO_CXP_USER_F() { return getEnvVar('FEEDBACK_URL_NO_CXP_USER_F'); },

  // ===== CXPADMIN SPECIFIC =====
  get LAYOUT_STRING() { return getEnvVar('LAYOUT_STRING'); },

  // ===== MISC =====
  get ONTARIO_CHAT_URL() { return getEnvVar('ONTARIO_CHAT_URL'); },
  get REACT_APP_API_URL() { return getEnvVar('REACT_APP_API_URL'); },
  get REACT_APP_SCOPES() { return getEnvVar('REACT_APP_SCOPES'); },
  get MaintenanceMode() { return getEnvVar('MaintenanceMode'); },
  get IRP_STATUS() { return getEnvVar('IRP_STATUS'); },
  get TPON_BD_URL() { return getEnvVar('TPON_BD_URL'); },
  get BXP_WEB_HOST() { return getEnvVar('BXP_WEB_HOST'); },
};

/**
 * Debug helper - logs all available environment variables
 * Only use in development!
 */
export const debugEnvConfig = (): void => {
  if (process.env.NODE_ENV === 'development') {
    console.group('Runtime Environment Configuration');
    console.log('window.__ENV__:', window.__ENV__ || '(not set - using process.env)');
    console.log('Source:', window.__ENV__ ? 'Runtime (env.js)' : 'Build-time (webpack)');
    console.groupEnd();
  }
};

export default ENV;

